import { Color, Size } from 'lib/styles';
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import Checkbox from 'components/UI/Checkbox';
import { Background, Border, Main } from 'styles/themeColors';
import EditIcon from '@mui/icons-material/Edit';
import Tooltip from './Tooltip';

const Td = styled.td<any>`
  border: ${({ $border }) => $border};
  margin: ${({ $margin }) => $margin};
  border-top-width: 0px !important;
  border-right-width: 0px !important;
  border-left-width: 0px !important;
  color: ${({ color }) => color};
  padding: 0px;
  position: relative;
  border-right-width: 0;
  white-space: nowrap;

  &:nth-last-child(2) {
    border-right-width: 1px;
  }

  ${props => props.error && css<any>`
    border-radius: ${({ $borderRadius }) => $borderRadius} !important;
    background-color: ${Color.errorRedTransparent};
    border: 1px solid ${Main.error};
  `}
`;

const TdContent = styled.div`
  align-items: center;
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: space-between;
`;

const TdContentCheckbox = styled.div`
  align-items: vertical;
  display: flex;
  justify-content: flex-start;
  color: ${({ color }) => color};
`;

const SharedCellStyles = css<any>`
  background-color: transparent;
  box-sizing: border-box;
  color: inherit;
  font-size: 14px;
  line-height: 1.5;
  border-radius: ${({ $borderRadius }) => $borderRadius};
  padding: ${({ $padding }) => $padding};
  min-width: ${({ $minWidth }) => $minWidth};
  max-width: ${({ $maxWidth }) => $maxWidth};
  font-weight: ${({ $fontWeight }) => $fontWeight};
`;

const Input = styled.input<any>`
  ${SharedCellStyles}
  border: none;
  width: 100%;
`;

const UnfocusedCell = styled.div<any>`
  ${SharedCellStyles}
  flex: 1;
  min-height: 30px; /* Ensure a minimum height for empty cells */
  cursor: pointer;
  display: flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export interface Props {
    error: boolean;
    color?: string;
    onChange: (e: any, column: string) => void;
    onBlurHandler?: (value: string) => void;
    value: string | number | boolean;
    columnIndex: number;
    disabled?: boolean;
    textColor?: string,
    className?: string,
    type?: string;
    column?: string;
    padding?: string;
    border?: string;
    showEditIcon?: boolean;
    minWidth?: string;
    maxLength?: number;
    errorMessage?: string;
    borderRadius?: string;
    emptyCell?: boolean;
    hideTooltip?: boolean;
    isTableCell?: boolean;
    editIconColor?: string;
    fontWeight?: string;
    editIconMargin?: string;
    margin?: string;
};

function TableEditableCell({
    error,
    color,
    onChange,
    onBlurHandler,
    value: propValue,
    columnIndex,
    disabled,
    textColor,
    className,
    type = 'text',
    column,
    padding = `${Size.tiny} ${Size.small}`,
    border = `1px solid ${Border.light}`,
    showEditIcon = false,
    minWidth = '100%',
    maxLength = 1000,
    errorMessage,
    borderRadius = '0px',
    emptyCell = true,
    hideTooltip = true,
    isTableCell = true,
    editIconColor = Main.brandDark,
    fontWeight = '',
    editIconMargin = '2px 5px',
    margin = '0px'
}: Props) {
    const [value, setValue] = useState(propValue);
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        setValue(propValue);
    }, [propValue]);

    function inputRef(element: HTMLInputElement) {
        if (element && isTableCell) {
            element.style.width = '0';
            element.style.minWidth = '0';
            element.style.width = `${element.scrollWidth}px`;
            element.style.minWidth = '100%';
        }
    }

    function onBlur(e: React.FormEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        setFocused(false);
        onChange(value, column!);
        onBlurHandler?.(value);
    }

    function onChangeWrapper(e: React.FormEvent<HTMLInputElement>) {
        setValue(e.currentTarget.value);
        if (e.currentTarget.value !== propValue) {
            onChange(e.currentTarget.value, column!);
        }
    }

    function onFocus(e: React.FocusEvent<HTMLInputElement>) {
        e.target.select();
    }

    let contents = (
        <TdContent>
            <UnfocusedCell
                onClick={() => !disabled && setFocused(true)}
                onFocus={() => !disabled && setFocused(true)}
                tabIndex={0}
                $padding={padding}
                $borderRadius={borderRadius}
                $fontWeight={fontWeight}
                aria-label={column ? `Edit ${column}` : "Edit cell"}
                style={{ cursor: !disabled ? 'pointer' : 'default' }}
            >
                {value ? value : emptyCell ? '' : 'N/A'}
            </UnfocusedCell>
            {
                showEditIcon && !disabled &&
                <EditIcon
                    style={{
                        cursor: 'pointer',
                        fontSize: 18,
                        margin: editIconMargin,
                        flexShrink: 0
                    }}
                    onClick={() => setFocused(true)}
                    htmlColor={editIconColor}
                />
            }
        </TdContent>
    );

    if (type === 'boolean') {
        contents = (
            <TdContentCheckbox>
                <Checkbox
                    id={`table-editable-cell-checkbox-${column}-${columnIndex}`}
                    checkboxSize="xs"
                    appearance="none"
                    checked={!!value}
                    onChange={value => onChange(value, column!)}
                    theme={value ? 'lightGray' : 'lightGray'}
                    bgColor={value ? Main.darkGray : Background.light}
                    disabled={disabled}
                />
            </TdContentCheckbox>
        );
    }

    if (focused && type !== 'checkbox' && !disabled) {
        contents = (
            <TdContent>
                <Input
                    ref={inputRef}
                    onChange={onChangeWrapper}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    type={type}
                    value={value! as string}
                    autoFocus
                    width={minWidth}
                    maxLength={maxLength}
                    disabled={disabled}
                    className={'focused-input-field'}
                    $padding={padding === '0px !important' && focused ? '0px 1px !important' : padding}
                    $minWidth={minWidth}
                    $maxWidth={'200px'}
                    $borderRadius={borderRadius}
                />
            </TdContent>
        );
    }

    return (
        <Td
            columnIndex={columnIndex}
            error={error}
            className={error ? 'warning-td' : className ? className : 'primary-td'}
            color={textColor || null}
            $border={border}
            $borderRadius={borderRadius}
            $margin={margin}
        >
            <Tooltip hidden={!error || hideTooltip} content={errorMessage}>
                {contents}
            </Tooltip>
        </Td>
    );
};

export default React.memo(TableEditableCell);